import { getDecimalDigits } from 'commerce-products-lib/utils/currency';
import { getProperty } from 'commerce-products-lib/utils/objectAccessors';
import { toNumber } from 'commerce-products-lib/utils/toNumber';
import { FIXED, PERCENT } from 'products-ui-components/constants/Discounts';
import { roundDecimals } from './rounding';

/** @TODO emitted-types should we allow undefined? */
const isDefined = discountValue => discountValue != null;
export const getDiscountType = (discount, discountPercentage) => {
  if (isDefined(discount)) {
    return FIXED;
  } else if (isDefined(discountPercentage)) {
    return PERCENT;
  }
  return null;
};
export const getSelectedDiscountValue = (discount, discountPercentage, discountType) => {
  if (discountType === FIXED) {
    return discount;
  } else if (discountType === PERCENT) {
    return discountPercentage;
  }
  return 0;
};

/**
 * Converts a per-unit, fixed discount to a percentage discount and vice versa
 * for a single line item.
 *
 * @param {number} discountValue - The discount that we want to convert into the
 * other type. If we want to convert fixed to percent, this is the fixed
 * discount (eg. 120 represents a $120 discount). If we want to convert percent
 * to fixed, this is the percent discount (eg. 15 represents 15% discount).
 * @param {number} price - The unit of price of the line item.
 * @param {string} from - The discount type we want to convert from. This should
 * match the type provided via discount param
 * @param {string} to - The discount type we want to conver to.
 *
 * @return {number} The converted discount value.
 *
 * @example convertDiscountValue({ discount: 1, price: 10, from: FIXED, to: PERCENT }) === 10
 * @example convertDiscountValue({ discount: 10, price: 10, from: PERCENT, to: FIXED }) === 1
 */

export const convertDiscountValue = ({
  discount,
  price,
  from,
  to
}) => {
  if (!discount || !price) {
    return 0;
  }
  if (from === to) {
    return discount;
  }
  if (from === FIXED && to === PERCENT) {
    return discount / price * 100;
  } else if (from === PERCENT && to === FIXED) {
    return discount / 100 * price;
  }
  throw new Error(`convertDiscountValue: invalid from or to params ${{
    from,
    to
  }}`);
};
export const calculateTotalDiscount = (quantity, price, discount, discountType = FIXED, decimalDigits = 2) => {
  if (discountType === FIXED) {
    return roundDecimals(discount * quantity, decimalDigits);
  } else if (discountType === PERCENT) {
    const totalPrice = price * quantity;
    return roundDecimals(totalPrice * discount / 100, decimalDigits);
  }
  return 0;
};

/**
 * Util that returns an unrounded, discounted unit price based on discountType
 *
 * @param {number} price
 * @param {number} discount
 * @param {string} discountType
 *
 * @returns {number} The unrounded, discounted unit price
 */
export const calculateRawDiscountedUnitPrice = (price, discount, discountType = FIXED) => {
  if (price) {
    if (!discount) {
      return price;
    }
    if (discountType === FIXED) {
      return price - discount;
    } else if (discountType === PERCENT) {
      return price - price * discount / 100;
    }
  }
  return price;
};

/**
 * @param {number} quantity
 * @param {number} price
 * @param {number} discount
 * @param {string} discountType
 * @param {number} decimalDigits
 *
 * @returns {number} The total rounded, discounted line item price taking into account quantity and decimal digits for the currency
 */
export const calculateBaseDiscountedLineItemPrice = (quantity, price, discount, discountType = FIXED, decimalDigits = 2) => {
  // Net price = pre discount amount - total discount
  // pre discount amount = round(unit price * qty)
  // total discount = round(pre discount amount * %) or round(flat discount * qty)

  const preDiscountAmount = roundDecimals(quantity * price, decimalDigits);
  return preDiscountAmount - calculateTotalDiscount(quantity, price, discount, discountType, decimalDigits);
};
export const calculateLineItemDiscount = lineItem => {
  var _lineItemCurrencyCode;
  const price = getProperty(lineItem, 'price');
  const quantity = getProperty(lineItem, 'quantity');
  const discount = getProperty(lineItem, 'discount');
  const discountPercentage = getProperty(lineItem, 'hs_discount_percentage');
  const lineItemCurrencyCode = getProperty(lineItem, 'hs_line_item_currency_code');
  const discountType = getDiscountType(discount, discountPercentage);
  const discountValue = getSelectedDiscountValue(discount !== null && discount !== void 0 ? discount : null, discountPercentage !== null && discountPercentage !== void 0 ? discountPercentage : null, discountType);
  const decimalDigits = getDecimalDigits({
    currencyCode: (_lineItemCurrencyCode = lineItemCurrencyCode === null || lineItemCurrencyCode === void 0 ? void 0 : lineItemCurrencyCode.toString()) !== null && _lineItemCurrencyCode !== void 0 ? _lineItemCurrencyCode : ''
  });
  return calculateTotalDiscount(toNumber(quantity), toNumber(price), toNumber(discountValue), discountType, decimalDigits);
};
export const calculateLineItemUnitDiscount = lineItem => {
  var _lineItemCurrencyCode2;
  const price = getProperty(lineItem, 'price');
  const discount = getProperty(lineItem, 'discount');
  const discountPercentage = getProperty(lineItem, 'hs_discount_percentage');
  const lineItemCurrencyCode = getProperty(lineItem, 'hs_line_item_currency_code');
  const discountType = getDiscountType(discount, discountPercentage);
  const discountValue = getSelectedDiscountValue(discount !== null && discount !== void 0 ? discount : null, discountPercentage !== null && discountPercentage !== void 0 ? discountPercentage : null, discountType);
  const decimalDigits = getDecimalDigits({
    currencyCode: (_lineItemCurrencyCode2 = lineItemCurrencyCode === null || lineItemCurrencyCode === void 0 ? void 0 : lineItemCurrencyCode.toString()) !== null && _lineItemCurrencyCode2 !== void 0 ? _lineItemCurrencyCode2 : ''
  });
  return calculateTotalDiscount(1, toNumber(price), toNumber(discountValue), discountType, decimalDigits);
};